import { inject, Injectable, OnDestroy } from '@angular/core';
import { getJawByToothNumber } from '@modules/teeth-diagram/models/teeth-numbering';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { LocalSettingsQuery } from '@shared/store/localSettings/local-settings-query';
import { LocalSettingsStore } from '@shared/store/localSettings/local-settings-store';
import { requireNonNull } from '@shared/utils/type-util';
import { combineLatest, map, Observable, shareReplay, Subject } from 'rxjs';
import {
	IntuitiveIndication,
	IntuitiveToothForDiagram,
	IntuitiveToothForTreatment,
	DiagramToothAndBridgeListViewItem,
	TeethNumberingSystem
} from './models';
import { IntuitiveBridge, IntuitiveBridgeValidationErrors } from './models/intuitive-bridge';
import { IRxIntuitiveTeethOrchestratorFacade } from './models/rx-intuitive-teeth-orchestrator-facade.interface';
import { fromUnitTypeInBridge } from './models/intuitive-indication';
import { RxIntuitiveTeethHelperService } from './services/rx-intuitive-teeth-helper.service';
import { RxIntuitiveBridgeService } from './services/rx-intuitive-bridge-service';
import { RxIntuitiveBridgeValidationService } from './services/rx-intuitive-bridge-validation.service';
import { RxIntuitiveIndicationRulesService } from './services/rx-intuitive-indication-rules.service';
import { RxIntuitiveTeethDiagramQuery } from './state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from './state/rx-intuitive-teeth-diagram-store';
import { RxIntuitiveTeethDiagramViewService } from './services/teeth-diagram/rx-intuitive-teeth-diagram-view.service';
import { RxIntuitiveTeethDiagramMutationService } from './services/teeth-diagram/rx-intuitive-teeth-diagram-mutation.service';

@Injectable()
export class RxIntuitiveTeethOrchestratorBridgeFacade implements IRxIntuitiveTeethOrchestratorFacade, OnDestroy {
	readonly facadeName = 'RxIntuitiveTeethOrchestratorBridgeFacade';

	// Structural compatibility with RxIntuitiveTeethDiagramFacade — these
	// members exist on the base class and are required when the bridge
	// orchestrator overrides `facade` with this type.
	/** @internal — keeps structural parity with RxIntuitiveTeethDiagramFacade */
	protected readonly facadeDestroy$ = new Subject<void>();
	protected readonly previewTeeth$: Observable<IntuitiveToothForTreatment[]> = this.teethHelper.previewTeeth$;

	// ── Bridge-only injections ────────────────────────────────────────────────
	private readonly validationService = inject(RxIntuitiveBridgeValidationService);
	private readonly localSettingsStore = inject(LocalSettingsStore);
	private readonly localSettingsQuery = inject(LocalSettingsQuery);

	// ── IRxIntuitiveTeethDiagramFacade — shared pass-throughs ────────────────
	readonly teethNumberingSystem$ = this.teethHelper.teethNumberingSystem$;
	readonly bridges$ = this.teethHelper.bridges$;
	readonly selectedToothIds$ = this.viewService.selectedToothIds$;
	readonly hasSelectedTeeth$ = this.selectedToothIds$.pipe(map(ids => ids.length > 0));
	readonly isToothEditorShown$: Observable<boolean> = this.teethHelper.isToothEditorShown$;
	readonly previewTreatmentListItems$: Observable<DiagramToothAndBridgeListViewItem[]> = this.teethHelper.previewTreatmentListItems$;
	readonly isTeethDiagramReadOnly$: Observable<boolean> = this.viewService.isTeethDiagramReadOnly$;
	readonly indicationToolbarIds$: Observable<IntuitiveIndication[]> = this.viewService.indicationToolbarIds$;
	readonly fetchRxAndSyncStore$ = this.viewService.fetchRxAndSyncStore$;

	// ── Bridge-only observables ───────────────────────────────────────────────
	readonly selectedBridge$: Observable<IntuitiveBridge> = this.teethHelper.selectedBridgeNonNull$;

	private readonly validationError$: Observable<IntuitiveBridgeValidationErrors | undefined> = combineLatest([
		this.intuitiveQuery.isSelectedBridgeSubmitted$,
		this.selectedBridge$
	]).pipe(
		map(([isSubmitted, bridge]) => {
			if (isSubmitted) {
				return this.validationService.validateBridge(bridge);
			}

			return undefined;
		})
	);

	readonly validationErrorSummary$: Observable<string | undefined> = this.validationError$.pipe(
		map(validationErrors => {
			// Business requirements are to show only one error message at a time
			return validationErrors?.summary?.[0];
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly showGuidanceBanner$: Observable<boolean> = combineLatest([
		this.selectedBridge$,
		this.intuitiveQuery.dismissedGuidanceBanner$,
		this.localSettingsQuery.userSettings$,
		this.validationErrorSummary$,
		this.isTeethDiagramReadOnly$
	]).pipe(
		map(([, dismissed, userSettings, validationErrorSummary, isReadonly]) => {
			if (!!validationErrorSummary || dismissed || isReadonly) {
				return false;
			}

			return !userSettings?.[this.shellQuery.contactId ?? 0]?.dontShowBridgeGuidanceBanner;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly hasUnsavedChanges$: Observable<boolean> = combineLatest([this.selectedBridge$, this.teethDiagramQuery.teethFlat$]).pipe(
		map(([selectedBridge, teethFlat]) => {
			const originalBridgeState = this.bridgeService.getBridgeSpanByBridgeIndex(selectedBridge.index, teethFlat);

			return originalBridgeState ? this.bridgeService.hasUnsavedChanges(selectedBridge, originalBridgeState) : false;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * All bridge indications that are renderable for the current procedure.
	 * Independent of tooth selection — buttons not in {@link enabledIndications$} are shown disabled.
	 */
	readonly visibleIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$
	]).pipe(
		map(([procedureMap, rxConfiguration]) => this.indicationsService.getBridgeProcedureIndications(procedureMap, rxConfiguration)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * Subset of {@link visibleIndications$} compatible with all currently-selected teeth inside the bridge.
	 *
	 * Source of truth is `selectedBridge.teeth` — not `teethDiagramQuery.teethRecord`, since the bridge
	 * has not yet been persisted when the user is editing it.
	 */
	readonly enabledIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.selectedBridge$,
		this.selectedToothIds$,
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$
	]).pipe(
		map(([bridge, selectedToothIds, procedureMap, rxConfiguration]) => {
			const selectedBridgeTeeth = bridge.teeth.filter(t => t.ToothID !== null && selectedToothIds.includes(t.ToothID));

			if (selectedBridgeTeeth.length === 0) {
				return [];
			}

			return this.indicationsService.getCommonBridgeIndicationsForTeeth(selectedBridgeTeeth, bridge, procedureMap, rxConfiguration);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly selectedTeethIndicationIds$: Observable<IntuitiveIndication[]> = combineLatest([
		this.selectedToothIds$,
		this.teethHelper.selectedBridgeTeeth$
	]).pipe(
		map(([selectedToothIds, bridgeTeeth]) => {
			if (selectedToothIds.length === 0) {
				return [];
			}

			const indications = selectedToothIds
				.map(id => {
					const tooth = bridgeTeeth.find(t => t.ToothID === id);

					return tooth?.ToothInBridgeTypeID != null ? fromUnitTypeInBridge(tooth.ToothInBridgeTypeID) : null;
				})
				.filter((ind): ind is IntuitiveIndication => ind != null);

			if (indications.length === 0) {
				return [];
			}

			const first = indications[0];

			return indications.every(id => id === first) ? [first] : [];
		})
	);

	readonly upperJawBridges$: Observable<Record<number, IntuitiveBridge>> = combineLatest([
		this.teethDiagramQuery.upperJaw$,
		this.selectedBridge$
	]).pipe(
		map(([jaw, selectedBridge]) => this.bridgeService.getAllBridgesForJawIntuitive(jaw, selectedBridge)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly lowerJawBridges$: Observable<Record<number, IntuitiveBridge>> = combineLatest([
		this.teethDiagramQuery.lowerJaw$,
		this.selectedBridge$
	]).pipe(
		map(([jaw, selectedBridge]) => this.bridgeService.getAllBridgesForJawIntuitive(jaw, selectedBridge)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly upperJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.upperJaw$,
		this.selectedToothIds$,
		this.teethHelper.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.upperJawBridges$,
		this.selectedBridge$,
		this.validationError$,
		this.isTeethDiagramReadOnly$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges, selectedBridge, validationError, isReadOnly]) =>
			this.mapJawTeeth(jaw, selectedToothIds, teethNumberingSystem, bridges, selectedBridge, validationError, isReadOnly)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly lowerJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.lowerJaw$,
		this.selectedToothIds$,
		this.teethHelper.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.lowerJawBridges$,
		this.selectedBridge$,
		this.validationError$,
		this.isTeethDiagramReadOnly$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges, selectedBridge, validationError, isReadOnly]) =>
			this.mapJawTeeth(jaw, selectedToothIds, teethNumberingSystem, bridges, selectedBridge, validationError, isReadOnly)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly canRemoveSelectedTooth$: Observable<boolean> = combineLatest([
		this.intuitiveQuery.selectedBridge$,
		this.selectedToothIds$
	]).pipe(
		map(([bridge, selectedToothIds]) => !!bridge && this.bridgeService.canRemoveToothFromBridge(bridge, selectedToothIds)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		protected readonly teethHelper: RxIntuitiveTeethHelperService,
		protected readonly viewService: RxIntuitiveTeethDiagramViewService,
		protected readonly mutationService: RxIntuitiveTeethDiagramMutationService,
		protected readonly bridgeService: RxIntuitiveBridgeService,
		protected readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		protected readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		protected readonly teethDiagramQuery: TeethDiagramQuery,
		protected readonly teethDiagramStore: TeethDiagramStore,
		protected readonly shellQuery: ShellQuery,
		protected readonly orderQuery: OrderQuery,
		protected readonly indicationsService: RxIntuitiveIndicationRulesService
	) {}

	ngOnDestroy(): void {
		this.facadeDestroy$.next();
		this.facadeDestroy$.complete();
	}

	// ── IRxIntuitiveTeethDiagramFacade mutations (delegated) ──────────────────

	createBridge(selectedToothIds: number[]): void {
		this.mutationService.createBridge(selectedToothIds);
	}

	clearToothSelection(): void {
		this.mutationService.clearToothSelection();
	}

	copyTreatmentDetailsFromSourceTooth(sourceToothId: number): void {
		this.mutationService.copyTreatmentDetailsFromSourceTooth(sourceToothId);
	}

	toggleToothSelection(toothId: number): void {
		const selectedBridge = requireNonNull(this.intuitiveQuery.selectedBridge);
		const isInBridge = selectedBridge.teeth.some(t => t.ToothID === toothId);

		if (isInBridge) {
			this.toggleInBridgeSelection(toothId);

			return;
		}

		this.mutationService.addTeethInBridge(toothId, selectedBridge);
	}

	// ── Bridge-specific public API ────────────────────────────────────────────

	/**
	 * Selects a tooth inside the bridge for navigation/viewing purposes only.
	 * Does NOT expand the bridge. Used in readonly mode.
	 */
	selectToothInBridgeReadOnly(toothId: number): void {
		const selectedBridge = this.intuitiveQuery.selectedBridge;

		if (!selectedBridge) {
			return;
		}

		const isInBridge = selectedBridge.teeth.some(t => t.ToothID === toothId);

		if (!isInBridge) {
			return;
		}

		this.mutationService.updateSelectedToothIds([toothId]);
	}

	saveBridge(): void {
		const currentBridge = requireNonNull(this.intuitiveQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		this.intuitiveStore.setBridgeSubmitted();

		const error = this.validationService.validateBridge(currentBridge);

		if (error.summary.length > 0 || Object.values(error.teeth).some(errors => errors.length > 0)) {
			return;
		}

		if (originalBridge) {
			const persistedTeeth = originalBridge.teeth.filter(t => t.ToothID != null).map(t => t.ToothID) as number[];

			this.teethDiagramStore.resetIndicationByIds(persistedTeeth, UnitTypes.Regular);
		}

		this.teethDiagramStore.updateTeeth({ teethToUpdate: currentBridge.teeth });
		this.deselectBridge();
	}

	revertChanges(): void {
		const currentBridge = requireNonNull(this.intuitiveQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		if (originalBridge != null) {
			return this.mutationService.selectBridge(originalBridge);
		}
	}

	closeBridge(): void {
		this.deselectBridge();
	}

	dismissGuidanceBanner(dontShowAgain: boolean): void {
		this.intuitiveStore.dismissGuidanceBanner();

		if (dontShowAgain) {
			this.localSettingsStore.updateLocalSettings(this.shellQuery.contactId ?? 0, { dontShowBridgeGuidanceBanner: true });
		}
	}

	removeToothFromBridge(): void {
		const bridge = this.intuitiveQuery.selectedBridge;
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;

		if (!bridge || !selectedToothIds.length) {
			return;
		}

		const wasRemovingFromLeft = selectedToothIds.includes(bridge.outerLeftToothId as number);
		const remainingTeeth = wasRemovingFromLeft
			? bridge.teeth.slice(selectedToothIds.length)
			: bridge.teeth.slice(0, bridge.teeth.length - selectedToothIds.length);

		const newOuterLeftToothId = remainingTeeth[0].ToothID;
		const newOuterRightToothId = remainingTeeth[remainingTeeth.length - 1].ToothID;
		const newFocusToothId = wasRemovingFromLeft ? newOuterLeftToothId : newOuterRightToothId;

		const updatedBridge: IntuitiveBridge = {
			...bridge,
			outerLeftToothId: newOuterLeftToothId,
			outerRightToothId: newOuterRightToothId,
			teeth: remainingTeeth
		};

		this.intuitiveStore.update({
			selectedBridge: updatedBridge,
			selectedToothIds: newFocusToothId ? [newFocusToothId] : []
		});
	}

	deselectBridge(): void {
		this.intuitiveStore.deselectBridge();
	}

	// ── Private helpers ───────────────────────────────────────────────────────

	private mapJawTeeth(
		jaw: Tooth[],
		selectedToothIds: number[],
		teethNumberingSystem: TeethNumberingSystem,
		bridges: Record<number, IntuitiveBridge>,
		selectedBridge: IntuitiveBridge,
		validationError: IntuitiveBridgeValidationErrors | undefined,
		isReadOnly = false
	): IntuitiveToothForDiagram[] {
		const existingBridges = this.filterOriginalBridgeOut(bridges, selectedBridge);

		return jaw.map(t => {
			const { tooth: mappingTooth, bridge } = this.findToothBridge(t, selectedBridge, existingBridges);

			return {
				...this.viewService.toDiagramVM(mappingTooth, selectedToothIds, teethNumberingSystem, bridge, validationError),
				isDisabled: isReadOnly ? false : this.isToothDisabledForBridge(t, selectedBridge)
			};
		});
	}

	private findToothBridge(
		tooth: Tooth,
		selectedBridge: IntuitiveBridge | undefined,
		bridges: Record<number, IntuitiveBridge> | undefined
	) {
		const toothInSelectedBridge = selectedBridge?.teeth.find(bt => tooth.ToothID === bt.ToothID);

		if (toothInSelectedBridge) {
			return { tooth: toothInSelectedBridge, bridge: selectedBridge };
		}

		if (bridges && tooth.BridgeIndex != null) {
			return { tooth, bridge: bridges[tooth.BridgeIndex] };
		}

		return { tooth, bridge: undefined };
	}

	private toggleInBridgeSelection(toothId: number): void {
		const selectedBridgeTeeth = this.intuitiveQuery.selectedBridgeTeeth;
		const bridgeTooth = selectedBridgeTeeth.find(t => t.ToothID === toothId);

		if (!bridgeTooth) {
			return;
		}

		const selectedToothIds = this.intuitiveQuery.selectedToothIds;
		let next: number[];

		if (selectedToothIds.includes(toothId)) {
			next = selectedToothIds.filter(id => id !== toothId);
		} else if (!this.validationService.isDefaultBridgeIndication(bridgeTooth)) {
			next = [toothId];
		} else {
			next = selectedToothIds.filter(id => {
				const bt = selectedBridgeTeeth.find(t => t.ToothID === id);

				return bt ? this.validationService.isDefaultBridgeIndication(bt) : false;
			});
			next = [...next, toothId];
		}

		this.mutationService.updateSelectedToothIds(next);
	}

	private isToothDisabledForBridge(tooth: Tooth, selectedBridge: IntuitiveBridge): boolean {
		const bridgeToothId = selectedBridge.outerLeftToothId ?? selectedBridge.outerRightToothId;

		if (bridgeToothId == null || tooth.ToothID == null) {
			return false;
		}

		const toothJawType = getJawByToothNumber({ toothId: tooth.ToothID });
		const bridgeJawType = getJawByToothNumber({ toothId: bridgeToothId });

		if (toothJawType !== bridgeJawType) {
			return true;
		}

		if (selectedBridge.teeth.some(bt => bt.ToothID === tooth.ToothID)) {
			return false;
		}

		if (tooth.BridgeIndex != null && tooth.BridgeIndex !== selectedBridge.index) {
			return true;
		}

		return this.hasOtherBridgeBetweenToothAndSelectedBridge(tooth, selectedBridge, toothJawType);
	}

	private hasOtherBridgeBetweenToothAndSelectedBridge(
		tooth: Tooth,
		selectedBridge: IntuitiveBridge,
		jawType: 'upper' | 'lower'
	): boolean {
		const jaw = jawType === 'upper' ? this.teethDiagramQuery.teeth.upperJaw : this.teethDiagramQuery.teeth.lowerJaw;
		const toothIndex = jaw.findIndex(t => t.ToothID === tooth.ToothID);

		if (toothIndex === -1) {
			return false;
		}

		const leftIndex = selectedBridge.outerLeftToothId != null ? jaw.findIndex(t => t.ToothID === selectedBridge.outerLeftToothId) : -1;
		const rightIndex =
			selectedBridge.outerRightToothId != null ? jaw.findIndex(t => t.ToothID === selectedBridge.outerRightToothId) : -1;

		if (leftIndex - toothIndex > 0) {
			const teethBetween = jaw.slice(toothIndex + 1, leftIndex);

			return teethBetween.some(t => t.BridgeIndex != null && t.BridgeIndex !== selectedBridge.index);
		}

		if (toothIndex - rightIndex > 0) {
			const teethBetween = jaw.slice(rightIndex, toothIndex + 1);

			return teethBetween.some(t => t.BridgeIndex != null && t.BridgeIndex !== selectedBridge.index);
		}

		return false;
	}

	private filterOriginalBridgeOut(bridges: Record<number, IntuitiveBridge>, selectedBridge: IntuitiveBridge) {
		const bridgeEntries = Object.entries(bridges).filter(([, item]) => item.index !== selectedBridge.index);

		return Object.fromEntries(bridgeEntries);
	}
}
